import { useStored } from "../travel/tripStore";
import { BackupBar } from "@/components/BackupBar";
import { pnum } from "../converters/numberInput";
import { useMemo, useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { safeCsvText } from "../file/csvParse";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";
import { copyText } from "@/lib/copyText";

interface InventoryItem {
  id: number;
  name: string;
  category: string;
  location: string;
  quantity: string;
  lowStockAt: string;
}

const TOOL_INFO_CONTENT = {
  what: `Tracks household items by category and location, with a low-stock threshold that flags anything running low the next time you check.`,
  how: `Add items with a category, location, current quantity, and a low-stock threshold, and anything at or below that threshold is flagged automatically.`,
  tip: `Set low-stock thresholds based on how often you actually shop, not an arbitrary round number — if you shop weekly, a threshold that gives you at least a week's buffer avoids running out between trips.`,
};

export default function InventoryTracker() {
  const [items, setItems] = useStored<InventoryItem[]>("fdp-InventoryTracker-items-v1", [
    {
      id: 1,
      name: "Paper towels",
      category: "Household",
      location: "Pantry",
      quantity: "3",
      lowStockAt: "1",
    },
    {
      id: 2,
      name: "AA Batteries",
      category: "Electronics",
      location: "Drawer",
      quantity: "8",
      lowStockAt: "4",
    },
    {
      id: 3,
      name: "Canned tomatoes",
      category: "Food",
      location: "Pantry",
      quantity: "2",
      lowStockAt: "2",
    },
  ]);
  const [nextId, setNextId] = useStored("fdp-InventoryTracker-nextId-v1", 4);

  const addItem = () => {
    setItems([
      ...items,
      {
        id: nextId,
        name: "New Item",
        category: "",
        location: "",
        quantity: "1",
        lowStockAt: "1",
      },
    ]);
    setNextId(nextId + 1);
  };

  const removeItem = (id: number) => setItems(items.filter(i => i.id !== id));
  const update = (id: number, field: keyof InventoryItem, value: string) =>
    setItems(items.map(i => (i.id === id ? { ...i, [field]: value } : i)));

  const lowStockItems = useMemo(
    () =>
      items.filter(
        i => (pnum(i.quantity) || 0) <= (pnum(i.lowStockAt) || 0)
      ),
    [items]
  );

  const { logUsageNow } = useLogUsageOnAction();

  const exportCsv = async () => {
    const allowed = await logUsageNow();
    if (!allowed) return;
    const rows = [
      ["Item", "Category", "Location", "Quantity", "Low Stock At"],
      ...items.map(i => [
        i.name,
        i.category,
        i.location,
        i.quantity,
        i.lowStockAt,
      ]),
    ];
    void copyText(safeCsvText(rows), "Inventory copied as CSV");
  };


  return (
    <ToolContainer>
      {lowStockItems.length > 0 && (
        <NoticeBox className="p-4 mb-6">
          <div className="flex items-center gap-2 font-medium mb-2">
            <AlertTriangle className="h-4 w-4 text-primary" /> Low Stock (
            {lowStockItems.length})
          </div>
          <p className="text-sm">
            {lowStockItems.map(i => i.name).join(", ")}
          </p>
        </NoticeBox>
      )}

      <div className="space-y-2">
        <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_80px_100px_40px] gap-2 text-xs font-medium text-muted-foreground px-1">
          <span>Item</span>
          <span>Category</span>
          <span>Location</span>
          <span>Qty</span>
          <span>Low Stock At</span>
          <span></span>
        </div>
        {items.map(item => {
          const isLow =
            (pnum(item.quantity) || 0) <=
            (pnum(item.lowStockAt) || 0);
          return (
            <div
              key={item.id}
              className="grid grid-cols-2 md:grid-cols-[1fr_1fr_1fr_80px_100px_40px] gap-2 items-center"
            >
              <Input
                value={item.name}
                onChange={e => update(item.id, "name", e.target.value)}
                className={isLow ? "border-amber-400" : ""}
              />
              <Input
                value={item.category}
                onChange={e => update(item.id, "category", e.target.value)}
                placeholder="Category"
              />
              <Input
                value={item.location}
                onChange={e => update(item.id, "location", e.target.value)}
                placeholder="Location"
              />
              <Input
                value={item.quantity}
                onChange={e => update(item.id, "quantity", e.target.value)}
              />
              <Input
                value={item.lowStockAt}
                onChange={e => update(item.id, "lowStockAt", e.target.value)}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeItem(item.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
      </div>

      <div className="flex gap-3 mt-4">
        <Button variant="outline" onClick={addItem}>
          <Plus className="h-4 w-4 mr-2" /> Add Item
        </Button>
        <Button variant="outline" onClick={exportCsv}>
          Copy as CSV
        </Button>
      </div>
          <BackupBar tool="inventory-tracker" prefixes={["fdp-InventoryTracker-"]} session />
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
